import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { toast } from "sonner";
import {
  Brain, Trophy, MapPin, FireSimple, Moon, Warning, Sparkle,
  ArrowsClockwise, GearSix, Lightning, ShieldCheck, Scales,
} from "@phosphor-icons/react";
import { usePipeline, pipelineParams } from "../lib/pipeline";

export default function IntelligenceDashboard() {
  const { activePipelineSlug, activePipeline } = usePipeline();
  const [data, setData] = useState(null);
  const [comparative, setComparative] = useState(null);
  const [showComparative, setShowComparative] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/intelligence/dashboard", { params: pipelineParams(activePipelineSlug) });
      setData(data);
    } catch { /* */ } finally { setLoading(false); }
  }, [activePipelineSlug]);
  useEffect(() => { load(); }, [load]);

  const loadComparative = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/intelligence/comparative");
      setComparative(data);
      setShowComparative(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando comparativo");
    } finally { setBusy(false); }
  };

  const recomputeTerritories = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/intelligence/territory/recompute");
      toast.success(`Territorios recalculados: ${data.count}`);
      load();
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  const generateHighlights = async () => {
    setBusy(true);
    try {
      const payload = { window_days: 7 };
      if (activePipelineSlug && activePipelineSlug !== "all") {
        payload.pipeline_slug = activePipelineSlug;
      }
      const { data } = await api.post("/intelligence/collective/highlights", payload);
      if (data.empty) {
        toast.info(data.message || "Sin material para generar highlights todavía.");
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Highlights generados (${data.based_on_messages} mensajes)`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando highlights");
    } finally { setBusy(false); }
  };

  const runCycleNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/intelligence/cycle/run-now");
      toast.success(`Ciclo IA: ${data.processed_users} usuarios · ${data.territories_recomputed} territorios`);
      load();
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  if (loading || !data) return <div className="p-10 text-carbon-500">Cargando inteligencia comunitaria...</div>;

  return (
    <>
      <PageHeader
        label={`AI Intelligence · ${activePipeline.label}`}
        title="Community Intelligence"
        description="Leadership · Engagement · Territory · Emotional · Moderation. La IA detecta líderes, mide engagement, vigila riesgos y entiende territorios — y se justifica."
      >
        <button onClick={loadComparative} disabled={busy} data-testid="open-pipeline-comparative-btn" className="bg-carbon-200 text-carbon-900 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-300">
          <Scales size={14} /> Comparar pipelines
        </button>
        <button onClick={() => setShowConfig(true)} data-testid="open-intelligence-config" className="bg-carbon-200 text-carbon-900 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-300">
          <GearSix size={14} /> Config
        </button>
        <button onClick={recomputeTerritories} disabled={busy} className="bg-carbon-200 text-carbon-900 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-300" data-testid="recompute-territories-btn">
          <MapPin size={14} /> Recalcular territorios
        </button>
        <button onClick={generateHighlights} disabled={busy} className="bg-forest text-white px-3 py-2 rounded text-sm flex items-center gap-2 hover:opacity-90" data-testid="generate-highlights-btn">
          <Sparkle size={14} /> Highlights
        </button>
        <button onClick={runCycleNow} disabled={busy} className="bg-terracotta text-white px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover" data-testid="run-cycle-btn">
          <ArrowsClockwise size={14} /> Correr ciclo IA
        </button>
      </PageHeader>

      <PageBody>
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KPI testid="kpi-active-7d" label="Activos 7d" value={data.engagement_trends?.["7d"] || 0} accent="forest" />
          <KPI testid="kpi-active-14d" label="Activos 14d" value={data.engagement_trends?.["14d"] || 0} accent="forest" />
          <KPI testid="kpi-active-30d" label="Activos 30d" value={data.engagement_trends?.["30d"] || 0} accent="terracotta" />
          <KPI testid="kpi-high-risk" label="Riesgo moderación" value={data.high_risk?.length || 0} accent="signal" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Top leaders */}
          <Card className="p-6 lg:col-span-2" testid="top-leaders-card">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={18} className="text-terracotta" />
              <div>
                <div className="section-label">Leadership Intelligence</div>
                <h2 className="font-heading text-xl">Top líderes detectados</h2>
              </div>
            </div>
            <UserList users={data.top_leaders} scoreKey="leadership_score" tone="terracotta" emptyMsg="Aún sin líderes detectados. Corre el ciclo IA." />
          </Card>

          {/* Founders core */}
          <Card className="p-6" testid="founders-core-card">
            <div className="flex items-center gap-2 mb-4">
              <FireSimple size={18} className="text-terracotta" />
              <div>
                <div className="section-label">Founders core</div>
                <h2 className="font-heading text-xl">Engagement = core</h2>
              </div>
            </div>
            <UserList users={data.founders_core} scoreKey="engagement_score" tone="forest" emptyMsg="Sin founders core aún." />
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Territories */}
          <Card className="p-6 lg:col-span-2" testid="territories-card">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-forest" />
              <div>
                <div className="section-label">Territory Intelligence</div>
                <h2 className="font-heading text-xl">Salud por municipio</h2>
              </div>
            </div>
            {(!data.top_territories || data.top_territories.length === 0) ? (
              <div className="text-sm text-carbon-400">Sin territorios calculados aún. Pulsa "Recalcular territorios".</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.top_territories.map((t, idx) => (
                  <div key={idx} className="border border-carbon-200 rounded p-3" data-testid={`territory-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{t.municipality || "—"}</div>
                      <Badge tone="terracotta">{(t.municipality_health_score ?? 0).toFixed?.(2) || "0.00"}</Badge>
                    </div>
                    <div className="text-xs text-carbon-500 mb-2">{t.country}</div>
                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                      <Stat label="Activos" value={t.total_users} />
                      <Stat label="Embajadores" value={t.ambassador_count} />
                      <Stat label="Crecimiento" value={(t.territory_growth_score ?? 0).toFixed?.(2)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Latest highlight */}
          <Card className="p-6" testid="highlight-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={18} className="text-terracotta" />
              <div>
                <div className="section-label">Voz colectiva</div>
                <h2 className="font-heading text-xl">Último highlight</h2>
              </div>
            </div>
            {!data.latest_highlight ? (
              <div className="text-sm text-carbon-400">Aún sin highlights. Pulsa "Highlights" arriba.</div>
            ) : (
              <div>
                <p className="text-sm text-carbon-900 mb-3 whitespace-pre-wrap leading-relaxed">{data.latest_highlight.summary}</p>
                {data.latest_highlight.trends?.dominant_emotion && (
                  <div className="border-t border-carbon-200 pt-3 mt-3">
                    <div className="text-xs text-carbon-500 mb-1">Tendencia dominante</div>
                    <Badge tone="forest">{data.latest_highlight.trends.dominant_emotion}</Badge>
                    {data.latest_highlight.trends?.sentiment !== undefined && (
                      <span className="ml-2 font-mono text-xs">sentiment {Number(data.latest_highlight.trends.sentiment).toFixed(2)}</span>
                    )}
                  </div>
                )}
                <div className="text-[10px] font-mono text-carbon-500 mt-3">
                  {new Date(data.latest_highlight.created_at).toLocaleString()} · {data.latest_highlight.based_on_messages} mensajes
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Dormant */}
          <Card className="p-6" testid="dormant-card">
            <div className="flex items-center gap-2 mb-3">
              <Moon size={18} className="text-carbon-500" />
              <div>
                <div className="section-label">Engagement Intelligence</div>
                <h2 className="font-heading text-xl">Dormidos detectados</h2>
              </div>
            </div>
            <UserList users={data.dormant_candidates} scoreKey="engagement_score" tone="neutral" emptyMsg="Sin dormidos detectados." />
          </Card>

          {/* High risk moderation */}
          <Card className="p-6" testid="high-risk-card">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={18} className="text-signal" />
              <div>
                <div className="section-label">Moderation Intelligence</div>
                <h2 className="font-heading text-xl">Alto riesgo</h2>
              </div>
            </div>
            <UserList users={data.high_risk} scoreKey="moderation_risk_score" tone="signal" emptyMsg="Sin alertas de moderación." />
            <div className="mt-3 text-[10px] text-carbon-500">
              Auto-moderation: <span className="font-mono">{data.config?.moderation_thresholds?.auto_moderation_enabled ? "ON" : "OFF"}</span> ·
              flag ≥ {data.config?.moderation_thresholds?.flag_for_review} ·
              auto_remove ≥ {data.config?.moderation_thresholds?.auto_remove}
            </div>
          </Card>

          {/* Top ambassadors */}
          <Card className="p-6" testid="top-ambassadors-card">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className="text-terracotta" />
              <div>
                <div className="section-label">Ambassador Intelligence</div>
                <h2 className="font-heading text-xl">Top embajadores activos</h2>
              </div>
            </div>
            <UserList users={data.top_ambassadors} scoreKey="ambassador_score" tone="terracotta" scoreMax={100} emptyMsg="Sin embajadores activos." />
          </Card>
        </div>

        {/* Automated actions */}
        <Card className="p-6" testid="automated-actions-card">
          <div className="flex items-center gap-2 mb-3">
            <Lightning size={18} className="text-warning" />
            <div>
              <div className="section-label">AI Reasoning Layer</div>
              <h2 className="font-heading text-xl">Acciones automatizadas recientes</h2>
            </div>
          </div>
          {(!data.recent_actions || data.recent_actions.length === 0) ? (
            <div className="text-sm text-carbon-400 py-3">Sin acciones automatizadas aún.</div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto scrollbar-thin">
              {data.recent_actions.map((a) => (
                <div key={a.id} className="border border-carbon-200 rounded p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge tone={a.applied ? "forest" : "warning"}>{a.applied ? "aplicada" : "recomendada"}</Badge>
                    <Badge>{a.module}</Badge>
                    <Badge tone="terracotta">{a.action}</Badge>
                    <span className="text-[10px] font-mono text-carbon-500">→ {a.target}</span>
                    <span className="text-[10px] font-mono text-carbon-500 ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-carbon-700">{a.reason}</div>
                  <div className="text-[10px] font-mono text-carbon-500 mt-1">confianza {a.confidence?.toFixed?.(2)} {a.user_id && <Link to={`/users/${a.user_id}`} className="text-terracotta ml-2">→ usuario</Link>}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </PageBody>

      {showConfig && <ConfigModal onClose={() => setShowConfig(false)} onSaved={() => { setShowConfig(false); load(); }} />}
      {showComparative && comparative && (
        <ComparativeModal data={comparative} onClose={() => setShowComparative(false)} />
      )}
    </>
  );
}

function KPI({ label, value, accent = "neutral", testid }) {
  const colors = {
    forest: "text-forest border-forest/30",
    terracotta: "text-terracotta border-terracotta/30",
    signal: "text-signal border-signal/30",
    neutral: "text-carbon-900 border-carbon-200",
  };
  return (
    <div data-testid={testid} className={`bg-surface border ${colors[accent]} rounded-lg p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-carbon-500 mb-1">{label}</div>
      <div className={`font-heading text-3xl font-light tracking-tighter ${colors[accent]}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-carbon-100 rounded px-2 py-1.5">
      <div className="text-carbon-500">{label}</div>
      <div className="font-mono text-carbon-900">{value ?? 0}</div>
    </div>
  );
}

function UserList({ users, scoreKey, tone = "neutral", scoreMax = 1, emptyMsg }) {
  if (!users || users.length === 0) return <div className="text-sm text-carbon-400 py-3">{emptyMsg}</div>;
  return (
    <div className="space-y-2">
      {users.map((u) => {
        const raw = u[scoreKey] ?? 0;
        const display = scoreMax === 1 ? raw.toFixed(2) : `${raw}/${scoreMax}`;
        return (
          <Link key={u.id} to={`/users/${u.id}`} className="flex items-center gap-3 p-2.5 border border-carbon-200 rounded hover:border-terracotta transition-colors">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{u.name}</div>
              <div className="text-[10px] text-carbon-500 truncate">{u.origin_country} · {u.origin_municipality || u.current_city || "—"}</div>
            </div>
            <Badge tone={tone}>{display}</Badge>
          </Link>
        );
      })}
    </div>
  );
}

function ConfigModal({ onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/intelligence/config").then((r) => setCfg(r.data)); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/intelligence/config", {
        leadership_detected_threshold: parseFloat(cfg.leadership_detected_threshold),
        min_action_confidence: parseFloat(cfg.min_action_confidence),
        moderation_thresholds: cfg.moderation_thresholds,
        engagement_levels: cfg.engagement_levels,
      });
      toast.success("Configuración actualizada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="section-label mb-1">Intelligence Config</div>
        <h2 className="font-heading text-2xl mb-4">Thresholds y pesos configurables</h2>
        <div className="space-y-4">
          <div>
            <label className="section-label mb-1 block">Leadership detected threshold (0-1)</label>
            <input type="number" step="0.05" min="0" max="1" value={cfg.leadership_detected_threshold}
              onChange={(e) => setCfg({ ...cfg, leadership_detected_threshold: e.target.value })}
              data-testid="cfg-leadership-threshold"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="section-label mb-1 block">Min action confidence (0-1)</label>
            <input type="number" step="0.05" min="0" max="1" value={cfg.min_action_confidence}
              onChange={(e) => setCfg({ ...cfg, min_action_confidence: e.target.value })}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
          </div>
          <div className="border-t border-carbon-200 pt-3">
            <div className="section-label mb-2">Moderation</div>
            <label className="flex items-center gap-2 mb-3 text-sm">
              <input type="checkbox" checked={!!cfg.moderation_thresholds.auto_moderation_enabled}
                data-testid="cfg-auto-moderation"
                onChange={(e) => setCfg({ ...cfg, moderation_thresholds: { ...cfg.moderation_thresholds, auto_moderation_enabled: e.target.checked } })} />
              Auto-moderation enabled (auto-remove cuando riesgo ≥ threshold)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-carbon-500">flag_for_review</label>
                <input type="number" step="0.05" min="0" max="1" value={cfg.moderation_thresholds.flag_for_review}
                  onChange={(e) => setCfg({ ...cfg, moderation_thresholds: { ...cfg.moderation_thresholds, flag_for_review: parseFloat(e.target.value) } })}
                  className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs text-carbon-500">auto_remove</label>
                <input type="number" step="0.05" min="0" max="1" value={cfg.moderation_thresholds.auto_remove}
                  onChange={(e) => setCfg({ ...cfg, moderation_thresholds: { ...cfg.moderation_thresholds, auto_remove: parseFloat(e.target.value) } })}
                  className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
              </div>
            </div>
          </div>
          <div className="border-t border-carbon-200 pt-3">
            <div className="section-label mb-2">Engagement levels</div>
            <div className="grid grid-cols-3 gap-3">
              {["medium", "high", "core"].map((k) => (
                <div key={k}>
                  <label className="text-xs text-carbon-500">{k}</label>
                  <input type="number" step="0.05" min="0" max="1" value={cfg.engagement_levels[k]}
                    onChange={(e) => setCfg({ ...cfg, engagement_levels: { ...cfg.engagement_levels, [k]: parseFloat(e.target.value) } })}
                    className="w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="cfg-save-btn" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60">{busy ? "Guardando..." : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

function ComparativeModal({ data, onClose }) {
  const slugs = ["founder", "ambassador"];
  const fmt = (n, d = 2) => (typeof n === "number" ? n.toFixed(d) : "—");
  const pct = (n) => (typeof n === "number" ? `${(n * 100).toFixed(1)}%` : "—");
  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4" data-testid="pipeline-comparative-modal">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="section-label">Cross-Pipeline Analytics · FASE 6</div>
            <h2 className="font-heading text-2xl">Founders vs Ambassadors</h2>
            <p className="text-xs text-carbon-500 mt-1">Métricas lado a lado para detectar desbalances de captación y conversión.</p>
          </div>
          <button onClick={onClose} className="text-carbon-500 hover:text-carbon-900">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {slugs.map((slug) => {
            const d = data[slug] || {};
            const tone = slug === "founder" ? "terracotta" : "forest";
            return (
              <div key={slug} className="border border-carbon-200 rounded-lg p-4" data-testid={`comparative-${slug}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-lg capitalize">{slug}s</h3>
                  <Badge tone={tone}>{d.total_users || 0} usuarios</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <Row label="Activos (core/high/medium)" value={d.active_users ?? 0} />
                  <Row label="Activos 7d (inbound)" value={d.active_7d ?? 0} />
                  <Row label="Convertidos" value={d.converted_users ?? 0} />
                  <Row label="Tasa de conversión" value={pct(d.conversion_rate)} />
                  <Row label="Engagement promedio" value={fmt(d.avg_engagement_score)} />
                  <Row label="Referidos generados" value={d.total_referrals_sourced ?? 0} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-carbon-500 mt-4 font-mono">
          Tip: si un pipeline cae &gt;40% en conversión vs el otro, revisa los prompts o las fuentes de leads.
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-carbon-200 text-carbon-900 rounded hover:bg-carbon-300">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-carbon-100 pb-1.5 last:border-0">
      <span className="text-carbon-500 text-xs">{label}</span>
      <span className="font-mono text-carbon-900">{value}</span>
    </div>
  );
}

